import React, { memo, useEffect, useRef, useId, useState, useCallback } from "react";
import clsx from "clsx";
import { createPopper } from "@popperjs/core";
import type { Instance as PopperInstance, Placement } from "@popperjs/core";
import { AnimatePresence, motion } from "framer-motion";
import ReactDOM from "react-dom";

export type MenuItem =
  | {
      type?: undefined;
      label: string;
      icon?: React.ReactNode;
      onClick?: () => void;
      danger?: boolean;
      disabled?: boolean;
      children?: MenuItem[];
      shortcut?: string;
    }
  | {
      type: "separator";
    };

export type MenuProps = {
  open: boolean;
  anchorEl?: React.RefObject<HTMLElement>;
  items: MenuItem[];
  onClose?: () => void;
  theme?: "light" | "dark";
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  placement?: Placement;
  portalContainer?: HTMLElement; // <-- Adicione esta linha
};

type MenuItemButtonProps = {
  item: MenuItem;
  idx: number;
  theme: "light" | "dark";
  refCallback: (el: HTMLButtonElement | null) => void;
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  id?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
};
const MenuItemButton = memo(
  ({
    item,
    idx,
    theme,
    refCallback,
    onClick,
    onMouseEnter,
    onMouseLeave,
    id,
    children,
  }: MenuItemButtonProps & { children?: React.ReactNode }) => {
    if ("type" in item && item.type === "separator") return null;
    const hasSubmenu = !!item.children && item.children.length > 0;
    return (
      <>
        <div style={{ position: "relative" }}>
          <button
            key={idx}
            ref={refCallback}
            role="menuitem"
            aria-disabled={item.disabled || undefined}
            id={id}
            // Acessibilidade para submenu
            {...(hasSubmenu && {
              "aria-haspopup": "menu",
              "aria-expanded": children ? true : false,
            })}
            className={clsx(
              "w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors font-grotesk",
              "backdrop-blur-sm text-[15px] font-normal whitespace-normal break-words",
              item.danger
                ? theme === "dark"
                  ? "hover:bg-red-900/40 text-red-300"
                  : "hover:bg-gradient-to-r hover:from-red-100 hover:to-red-300 text-red-700"
                : theme === "dark"
                ? "hover:bg-zinc-800"
                : "hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100",
              "focus:outline-none transition-all duration-200",
              item.disabled && "opacity-50 pointer-events-none"
            )}
            style={{
              fontWeight: 400,
              letterSpacing: 0.01,
              border: "none",
              cursor: item.disabled ? "not-allowed" : "pointer",
              minHeight: 40,
              textAlign: "left",
            }}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            tabIndex={item.disabled ? -1 : 0}
            type="button"
            disabled={item.disabled}
          >
            {item.icon && (
              <span className="text-lg opacity-80 flex-shrink-0">{item.icon}</span>
            )}
            <span className="truncate whitespace-normal break-words">{item.label}</span>
            {item.shortcut && (
              <span className="ml-auto text-xs opacity-60 font-mono">{item.shortcut}</span>
            )}
            {hasSubmenu && (
              <span className="ml-2 opacity-60">&rsaquo;</span>
            )}
          </button>
          {/* Submenu inline */}
          {children}
        </div>
      </>
    );
  }
);
MenuItemButton.displayName = "MenuItemButton";

// Função utilitária para comparar atalhos
function matchShortcut(shortcut: string, e: KeyboardEvent) {
  const keys = shortcut.toLowerCase().split("+").map(k => k.trim());
  const ctrl = keys.includes("ctrl");
  const shift = keys.includes("shift");
  const alt = keys.includes("alt");
  const meta = keys.includes("meta") || keys.includes("cmd") || keys.includes("command");
  const mainKey = keys.find(k =>
    !["ctrl", "shift", "alt", "meta", "cmd", "command"].includes(k)
  );
  return (
    e.ctrlKey === ctrl &&
    e.shiftKey === shift &&
    e.altKey === alt &&
    e.metaKey === meta &&
    e.key.toLowerCase() === (mainKey || "").toLowerCase()
  );
}

/**
 * MenuItem representa um item de menu, podendo conter subitens (submenu), ícone, atalho de teclado e estado de perigo/desabilitado.
 *
 * @typedef {Object} MenuItem
 * @property {string} label - Texto exibido no item do menu.
 * @property {React.ReactNode} [icon] - Ícone opcional exibido ao lado do label.
 * @property {() => void} [onClick] - Função chamada ao clicar no item.
 * @property {boolean} [danger] - Indica se o item é perigoso (ex: deletar).
 * @property {boolean} [disabled] - Indica se o item está desabilitado.
 * @property {MenuItem[]} [children] - Subitens (submenu) deste item.
 * @property {string} [shortcut] - Atalho de teclado para acionar o item (ex: "Ctrl+S").
 *
 * @example
 * const item: MenuItem = {
 *   label: "Salvar",
 *   icon: <SaveIcon />,
 *   onClick: () => salvar(),
 *   shortcut: "Ctrl+S"
 * };
 */

/**
 * Propriedades do componente Menu.
 *
 * @typedef {Object} MenuProps
 * @property {boolean} open - Define se o menu está aberto.
 * @property {React.RefObject<HTMLElement>} [anchorEl] - Elemento de referência para posicionamento do menu.
 * @property {MenuItem[]} items - Lista de itens do menu.
 * @property {() => void} [onClose] - Função chamada ao fechar o menu.
 * @property {"light" | "dark"} [theme] - Tema visual do menu.
 * @property {string} [className] - Classe CSS adicional.
 * @property {React.CSSProperties} [style] - Estilo CSS adicional.
 * @property {string} [id] - ID do menu.
 * @property {Placement} [placement] - Posição do menu em relação ao anchorEl.
 * @property {HTMLElement} [portalContainer] - Container para portalizar o menu (útil para modais).
 *
 * @example
 * <Menu
 *   open={menuAberto}
 *   anchorEl={botaoRef}
 *   items={[
 *     { label: "Editar", onClick: () => editar() },
 *     { label: "Excluir", danger: true, onClick: () => excluir() }
 *   ]}
 *   onClose={() => setMenuAberto(false)}
 *   theme="dark"
 * />
 */

/**
 * Menu é um componente de menu contextual acessível, com suporte a submenus, atalhos de teclado, animações e temas claro/escuro.
 *
 * - Usa Popper.js para posicionamento dinâmico.
 * - Suporta navegação por teclado (setas, Enter, Escape, Tab, typeahead).
 * - Permite atalhos globais definidos nos itens.
 * - Suporta submenus aninhados, cada submenu também animado com framer-motion.
 * - Pode ser renderizado em portal (útil para modais).
 * - Animações de entrada/saída são aplicadas tanto ao menu principal quanto aos submenus usando `<motion.div>`.
 *
 * @component
 * @param {MenuProps} props
 * @returns {JSX.Element}
 *
 * @example
 * const items = [
 *   { label: "Novo", onClick: () => criarNovo() },
 *   { label: "Abrir", shortcut: "Ctrl+O", onClick: () => abrir() },
 *   {
 *     label: "Mais opções",
 *     children: [
 *       { label: "Configurações", onClick: () => abrirConfig() },
 *       { label: "Ajuda", onClick: () => abrirAjuda() }
 *     ]
 *   }
 * ];
 * <Menu open={aberto} anchorEl={refBotao} items={items} onClose={() => setAberto(false)} />
 */
const Menu: React.FC<MenuProps> = memo(({
  open,
  anchorEl,
  items,
  onClose,
  theme = "light",
  className,
  style,
  id,
  placement = "bottom-start",
  portalContainer,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const popperInstance = useRef<PopperInstance | null>(null);
  const autoId = useId();
  const menuId = id || `menu-root-${autoId}`;
  const labelId = `${menuId}-label`;

  const [submenuIdx, setSubmenuIdx] = useState<number | null>(null);

  // Popper.js para o menu principal (mantido)
  useEffect(() => {
    if (open && anchorEl?.current && menuRef.current) {
      popperInstance.current = createPopper(anchorEl.current, menuRef.current, {
        placement,
        modifiers: [
          { name: "offset", options: { offset: [0, 8] } },
          { name: "preventOverflow", options: { boundary: "viewport" } },
          { name: "flip", options: { fallbackPlacements: ["top-start", "right-start"] } },
        ],
      });
    }
    return () => {
      popperInstance.current?.destroy();
      popperInstance.current = null;
    };
  }, [open, anchorEl, placement]);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        anchorEl?.current &&
        !(e.target as HTMLElement).closest(`#${menuId}`)
      ) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, anchorEl, onClose, menuId]);

  // Fecha submenu ao sair do menu principal
  useEffect(() => {
    if (!open) setSubmenuIdx(null);
  }, [open]);

  // Gera um id único para cada item
  const getItemId = (idx: number) => `${menuId}-item-${idx}`;

  // Atalhos de teclado para itens do menu
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      for (const item of items) {
        if (
          item.type !== "separator" &&
          item.shortcut &&
          matchShortcut(item.shortcut, e) &&
          !item.disabled
        ) {
          e.preventDefault();
          item.onClick?.();
          onClose?.();
          break;
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, items, onClose]);

  // Navegação por teclado no menu principal
  const handleKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = (idx + 1) % items.length;
      itemRefs.current[next]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = (idx - 1 + items.length) % items.length;
      itemRefs.current[prev]?.focus();
    } else if (e.key === "ArrowRight") {
      if (
        items[idx].type !== "separator" &&
        items[idx].children &&
        items[idx].children.length > 0
      ) {
        setSubmenuIdx(idx);
        // Foca o primeiro item do submenu após abrir
        setTimeout(() => {
          const submenu = document.querySelector(
            `#${getItemId(idx)}-submenu button`
          ) as HTMLButtonElement | null;
          submenu?.focus();
        }, 0);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose?.();
    }
  };

  const handleItemClick = useCallback((item: MenuItem, idx: number) => {
    if (item.type !== "separator" && !item.disabled) {
      if (item.children && item.children.length > 0) {
        setSubmenuIdx(idx);
      } else {
        item.onClick?.();
        onClose?.();
      }
    }
  }, [onClose]);

  const closeSubmenuTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleItemMouseEnter = useCallback((item: MenuItem, idx: number) => {
    if ("children" in item && item.children && !item.disabled) {
      if (closeSubmenuTimeout.current) clearTimeout(closeSubmenuTimeout.current);
      setSubmenuIdx(idx);
    } else {
      setSubmenuIdx(null);
    }
  }, []);

  const handleMenuMouseLeave = () => {
    closeSubmenuTimeout.current = setTimeout(() => {
      setSubmenuIdx(null);
    }, 180); // tempo igual à animação do submenu
  };

  const handleMenuMouseEnter = () => {
    if (closeSubmenuTimeout.current) clearTimeout(closeSubmenuTimeout.current);
  };

  // Renderização do menu principal e submenus recursivos
  const menuContent = (
    <AnimatePresence>
      {open && (
        <motion.div
          id={menuId}
          ref={menuRef}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby={labelId}
          tabIndex={-1}
          className={clsx(
            "absolute z-50 mt-2 min-w-[180px] rounded-xl shadow-2xl border font-grotesk",
            theme === "dark"
              ? "bg-zinc-900/70 border-zinc-700 text-zinc-100"
              : "bg-white/70 border-gray-200 text-gray-900",
            "backdrop-blur-lg",
            className
          )}
          style={{
            position: "absolute",
            boxShadow:
              theme === "dark"
                ? "0 4px 24px 0 rgba(0,0,0,0.45)"
                : "0 4px 24px 0 rgba(0,0,0,0.10)",
            backdropFilter: "blur(16px) saturate(1.2)",
            WebkitBackdropFilter: "blur(16px) saturate(1.2)",
            border: theme === "dark" ? "1.5px solid #23242a" : "1.5px solid #e3e6ee",
            background:
              theme === "dark"
                ? "rgba(36,37,46,0.85)"
                : "rgba(255,255,255,0.85)",
            ...style,
          }}
          initial={{ opacity: 0, scale: 0.98, y: -8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98, y: -8 }}
          transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
          onMouseLeave={handleMenuMouseLeave}
          onMouseEnter={handleMenuMouseEnter}
        >
          <span id={labelId} className="sr-only">Menu</span>
          {items.map((item, idx) => (
            <MenuItemButton
              key={idx}
              item={item}
              idx={idx}
              theme={theme}
              refCallback={el => {
                itemRefs.current[idx] = el;
              }}
              onClick={() => handleItemClick(item, idx)}
              onMouseEnter={() => handleItemMouseEnter(item, idx)}
              id={getItemId(idx)}
              onKeyDown={e => handleKeyDown(e, idx)}
            >
              {/* Submenu recursivo usando o próprio Menu */}
              {item.type !== "separator" && item.children && submenuIdx === idx && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.98, x: -8 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.98, x: -8 }}
                  transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                  className="absolute left-full top-0"
                >
                  <Menu
                    open={true}
                    anchorEl={undefined}
                    items={item.children}
                    onClose={() => setSubmenuIdx(null)}
                    theme={theme}
                  />
                </motion.div>
              )}
            </MenuItemButton>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (portalContainer) return ReactDOM.createPortal(menuContent, portalContainer);
  return menuContent;
});

Menu.displayName = "Menu";
export default Menu;